import { agentEditorCopy } from '@/copy'
import type { FormState } from '@/types/editor'
import { type DiffLine, diffLines } from '@/utils/diff'

interface DiffRow {
  label: string
  from: string
  to: string
}

interface PromptDiff {
  added: number
  deleted: number
  /** add/del lines only — compact preview, unchanged lines omitted. */
  lines: DiffLine[]
}

export interface AgentDiff {
  rows: DiffRow[]
  capabilities: { added: string[]; removed: string[] }
  prompt: PromptDiff | null
  /** Distinct fields/sections changed, for the "(N)" header. */
  total: number
}

const SCALAR: { key: keyof FormState; label: string }[] = [
  { key: 'name', label: agentEditorCopy.diff.name },
  { key: 'model', label: agentEditorCopy.diff.model },
  { key: 'provider_id', label: agentEditorCopy.diff.provider },
  { key: 'description', label: agentEditorCopy.diff.description },
  { key: 'routing_hint', label: agentEditorCopy.diff.routingHint },
  { key: 'max_iterations', label: agentEditorCopy.diff.maxIterations },
  { key: 'timeout_ms', label: agentEditorCopy.diff.timeoutMs },
  { key: 'max_tokens', label: agentEditorCopy.diff.maxTokens },
  { key: 'max_cost_usd', label: agentEditorCopy.diff.maxCostUsd },
  { key: 'session_ttl_days', label: agentEditorCopy.diff.sessionTtl },
]

function show(v: string): string {
  const t = v.trim()
  return t === '' ? '—' : v
}

function parseCaps(value: string): string[] {
  return [
    ...new Set(
      value
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean),
    ),
  ]
}

function scheduleKey(e: { schedule: string; task: string }): string {
  return `${e.schedule.trim()} :: ${e.task.trim()}`
}

/**
 * Compute a structured change set between two form states. Pure so it can be
 * unit-tested; the panel renders from this. `base` is the snapshot to compare
 * against (last-saved agent, or the template a new agent was forked from).
 */
export function computeAgentDiff(
  base: FormState,
  current: FormState,
): AgentDiff {
  const rows: DiffRow[] = []

  for (const { key, label } of SCALAR) {
    const a = base[key] as string
    const b = current[key] as string
    if (a !== b) rows.push({ label, from: show(a), to: show(b) })
  }

  // Capabilities: set-diff the newline-joined lists.
  const baseCaps = new Set(parseCaps(base.capabilities))
  const curCaps = new Set(parseCaps(current.capabilities))
  const addedCaps = parseCaps(current.capabilities).filter(
    (c) => !baseCaps.has(c),
  )
  const removedCaps = parseCaps(base.capabilities).filter(
    (c) => !curCaps.has(c),
  )

  // System prompt: real line diff.
  let prompt: PromptDiff | null = null
  if (base.system_prompt !== current.system_prompt) {
    const lines = diffLines(base.system_prompt, current.system_prompt).filter(
      (l) => l.op !== 'eq',
    )
    prompt = {
      added: lines.filter((l) => l.op === 'add').length,
      deleted: lines.filter((l) => l.op === 'del').length,
      lines,
    }
  }

  // Nested config: per-field rows so the diff is greppable, not opaque blobs.
  // Absent (keep) and null (clear) both read as the model default.
  const reasoning = (f: FormState) =>
    f.reasoning ? JSON.stringify(f.reasoning) : '—'
  if (reasoning(base) !== reasoning(current)) {
    rows.push({
      label: agentEditorCopy.diff.reasoning,
      from: reasoning(base),
      to: reasoning(current),
    })
  }

  const cmpBase = base.compaction
  const cmpCur = current.compaction
  const cmpLabels: Record<keyof typeof cmpBase, string> = {
    max_context_tokens: agentEditorCopy.diff.compactionContextTokens,
    preserve_recent: agentEditorCopy.diff.compactionPreserveRecent,
    max_tool_result_tokens: agentEditorCopy.diff.compactionToolResultCap,
    summary_strategy: agentEditorCopy.diff.compactionSummaryStrategy,
  }
  for (const k of Object.keys(cmpBase) as (keyof typeof cmpBase)[]) {
    if (cmpBase[k] !== cmpCur[k]) {
      rows.push({
        label: cmpLabels[k],
        from: show(cmpBase[k]),
        to: show(cmpCur[k]),
      })
    }
  }

  // Schedule: no stable id, so key entries by cron :: task for a coarse diff.
  const baseSched = new Set(base.schedule.map(scheduleKey))
  const curSched = new Set(current.schedule.map(scheduleKey))
  for (const e of current.schedule) {
    const k = scheduleKey(e)
    if (!baseSched.has(k)) {
      rows.push({
        label: agentEditorCopy.diff.schedule,
        from: '—',
        to: e.schedule.trim() || agentEditorCopy.diff.noCron,
      })
    }
  }
  for (const e of base.schedule) {
    const k = scheduleKey(e)
    if (!curSched.has(k)) {
      rows.push({
        label: agentEditorCopy.diff.schedule,
        from: e.schedule.trim() || agentEditorCopy.diff.noCron,
        to: agentEditorCopy.diff.removed,
      })
    }
  }

  const capsChanged = addedCaps.length > 0 || removedCaps.length > 0
  const total = rows.length + (capsChanged ? 1 : 0) + (prompt ? 1 : 0)

  return {
    rows,
    capabilities: { added: addedCaps, removed: removedCaps },
    prompt,
    total,
  }
}
